import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

import { Item, MenuOptionItem, MenuOptionItemIntent, MenuTriggerButton, Section, useDialog } from "widgets";

import { urlHomePage } from "@/app-utils/classic-portal.ts";
import { getDocumentationLink, getRootDocumentationPage, getTipsAreOffTip } from "@/app-utils/help.tsx";
import { useRouteTo } from "@/app-utils/hooks/useRouteTo.ts";
import { capitalizeFirstLetter } from "@/app-utils/string.ts";
import type { Capability, Configuration, User } from "@/graphql.ts";
import { urls } from "@/modules/app/utils/urls.js";
import { checkCapability } from "@/modules/users/utils/user.js";
import { showGettingStartedTips, showIntroLocally, updateLocalUserPref } from "@/redux/modules/auth.ts";
import { isLicensed } from "@/redux/modules/config.ts";
import { downloadLogArchive } from "@/redux/modules/data-export.ts";
import { type INavbarState, showNavbarElement } from "@/redux/modules/navbar.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { CAPABILITY, GETTING_STARTED_ELEMENT_ID, LICENSE_MODULE } from "@/settings/settings.ts";
import TipAreOff from "../TipAreOff.js";
import { ApplicationInfoDialog } from "./ApplicationInfoDialog.js";

export function HelpMenu() {
  const dispatch = useDispatch<ReduxDispatch>();
  const location = useLocation();
  const routeTo = useRouteTo();

  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration, shallowEqual);
  const user = useSelector<IAppState, User>((state) => state.auth.session, shallowEqual);
  const authenticated = useSelector<IAppState, boolean>((state) => state.auth.isAuthenticated, shallowEqual);
  const navBar = useSelector<IAppState, INavbarState>((state) => state.navBar, shallowEqual);

  const application = useMemo(() => configuration?.application, [configuration]);
  const tipsShown = useMemo(() => user && (user.params?.showTips ?? false), [user]);
  const routeToDoc = useMemo(() => getDocumentationLink(location.pathname), [location.pathname]);
  const docRootUrl = useMemo(() => application?.documentationUrl + getRootDocumentationPage(), [application]);
  const docPageUrl = useMemo(() => application?.documentationUrl + routeToDoc.docPage, [application, routeToDoc]);
  const docPageLabel = useMemo(() => routeToDoc.label, [routeToDoc]);
  const hasRestAccess = useMemo(
    () => checkCapability(user?.capabilities as Capability[], CAPABILITY.REST_API_ACCESS),
    [user],
  );

  const {
    dialogRef: aboutDialogRef,
    dialogProps: aboutDialogProps,
    underlayProps: aboutDialogUnderlayProps,
    state: aboutDialogState,
  } = useDialog<HTMLDivElement>();

  return (
    <>
      <TipAreOff tip={getTipsAreOffTip(application?.name ?? "Selector")} style={{ display: "flex" }}>
        <MenuTriggerButton
          id={GETTING_STARTED_ELEMENT_ID.HELP_MENU_BUTTON}
          icon={"help"}
          label={"Help"}
          placement="bottom"
          onAction={(key) => {
            switch (key) {
              case "navbar-menus-doc":
                routeTo(docPageUrl);
                break;
              case "navbar-menus-doc-rest":
                routeTo(urls.xapi);
                break;
              case GETTING_STARTED_ELEMENT_ID.DOCUMENTATION:
                routeTo(docRootUrl);
                break;
              case "navbar-menus-shortcuts":
                dispatch(updateLocalUserPref({ showShortcutsHelp: true }));
                break;
              case "navbar-menus-tips":
                dispatch(showGettingStartedTips(!tipsShown));
                break;
              case "navbar-menus-intro":
                dispatch(showIntroLocally());
                break;
              case "navbar-menus-classicui":
                routeTo(urlHomePage(application));
                break;
              case "navbarmenus-audit":
                dispatch(showNavbarElement({ showAuditReportForm: !navBar.showAuditReportForm }));
                break;
              case "navbarmenus-logs":
                void dispatch(downloadLogArchive());
                break;
              case "navbarmenus-about":
                aboutDialogState.open();
                break;
            }
          }}
        >
          <Section title={"Documentation"} key="documentation">
            <Item key="navbar-menus-doc">
              <MenuOptionItem id="navbar-menus-doc" title={docPageLabel} icon={"library_books"} />
            </Item>
            <Item key={GETTING_STARTED_ELEMENT_ID.DOCUMENTATION}>
              <MenuOptionItem
                icon={"library_books"}
                id={GETTING_STARTED_ELEMENT_ID.DOCUMENTATION}
                title={"Documentation Library"}
              />
            </Item>
            {hasRestAccess && (
              <Item key={"navbar-menus-doc-rest"}>
                <MenuOptionItem icon={"data_object"} id={"navbar-menus-doc-rest"} title={"REST-API Documentation"} />
              </Item>
            )}
          </Section>
          {authenticated && (
            <Section title={"Help"} key="help">
              <Item key="navbar-menus-shortcuts">
                <MenuOptionItem
                  id="navbar-menus-shortcuts"
                  icon={"keyboard"}
                  title={"Keyboard Shortcuts"}
                  shortcut={"Ctrl+Shift+?"}
                />
              </Item>
              <Item key="navbar-menus-tips">
                <MenuOptionItem
                  id="navbar-menus-tips"
                  icon={"live_help"}
                  title={`${tipsShown ? "Hide" : "Show"} Getting Started Tips`}
                />
              </Item>
              <Item key="navbar-menus-intro">
                <MenuOptionItem
                  id="navbar-menus-intro"
                  icon={"live_help"}
                  title={`Introduction to ${capitalizeFirstLetter(application?.name || "Selector")}`}
                />
              </Item>
            </Section>
          )}

          {isLicensed(configuration, LICENSE_MODULE.EXPLORER) && user && (
            <Section key="classic">
              <Item key="navbar-menus-classicui">
                <MenuOptionItem id="navbar-menus-classicui" icon={"open_in_new"} title={"Access the Classic UI"} />
              </Item>
            </Section>
          )}
          {authenticated &&
            user &&
            (checkCapability(user.capabilities, CAPABILITY.DOWNLOAD_SYSTEM_LOGS) ||
              (checkCapability(user.capabilities, CAPABILITY.CREATE_AUDIT_REPORT) &&
                configuration?.auditLogEnabled)) && (
              <Section title={"System"} key="system">
                {checkCapability(user.capabilities, CAPABILITY.CREATE_AUDIT_REPORT) &&
                  configuration?.auditLogEnabled && (
                    <Item key="navbarmenus-audit">
                      <MenuOptionItem id="navbarmenus-audit" icon={"download"} title={"Download Audit Report"} />
                    </Item>
                  )}
                {checkCapability(user.capabilities, CAPABILITY.DOWNLOAD_SYSTEM_LOGS) && (
                  <Item key="navbarmenus-logs">
                    <MenuOptionItem id="navbarmenus-logs" icon={"download"} title={"Download Logs"} />
                  </Item>
                )}
              </Section>
            )}
          <Section key="section">
            <Item key="navbarmenus-about">
              <MenuOptionItem
                id="navbarmenus-about"
                icon={"info"}
                title={`About Genedata ${application?.name ?? "Selector"}`}
                intent={MenuOptionItemIntent.Primary}
              />
            </Item>
          </Section>
        </MenuTriggerButton>
      </TipAreOff>
      {aboutDialogState.isOpen && (
        <ApplicationInfoDialog
          underlayProps={aboutDialogUnderlayProps}
          domProps={aboutDialogProps}
          closeFn={aboutDialogState.close}
          ref={aboutDialogRef}
        />
      )}
    </>
  );
}

export function UserMenu() {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();
  const user = useSelector<IAppState, User>((state) => state.auth.session);
  const appName = useSelector<IAppState, string>((state) => state.config.configuration?.application?.name ?? "");

  if (!user) return null;

  return (
    <MenuTriggerButton
      icon={"account_circle"}
      label={user.display}
      placement="bottom"
      onAction={(key) => {
        switch (key) {
          case "navbarmenus-user":
            navigate("/user");
            break;
          case "navbarmenus-logout":
            navigate("/logout");
            break;
          case "navbarmenus-admin":
            navigate("/admin");
            break;
          case "navbarmenus-files":
            dispatch(showNavbarElement({ showFileBrowser: true }));
            break;
          case "navbarmenus-tracing":
            navigate("/trace");
            break;
          case "navbarmenus-error":
            navigate("/errors");
            break;
        }
      }}
    >
      <Section title={"User"}>
        <Item key="navbarmenus-user">
          <MenuOptionItem
            id="navbarmenus-user"
            icon={"person"}
            description={`(${user.username})`}
            title={user.display}
          />
        </Item>
        <Item key="navbarmenus-logout">
          <MenuOptionItem
            id="navbarmenus-logout"
            icon={"logout"}
            description={`Log out of Genedata ${appName}`}
            intent={MenuOptionItemIntent.Danger}
            title={"Logout"}
          />
        </Item>
      </Section>
      {checkCapability(user.capabilities, CAPABILITY.ADMINISTRATION) && (
        <Section title={"Utilities"}>
          <Item key="navbarmenus-admin">
            <MenuOptionItem id="navbarmenus-admin" icon={"admin_panel_settings"} title={"Administration"} />
          </Item>
        </Section>
      )}
      {!!import.meta.env.DEV && (
        <Section title={"Development"}>
          <Item key="navbarmenus-files">
            <MenuOptionItem id="navbarmenus-files" icon={"folder_open"} title={"Server File Browser"} />
          </Item>
          <Item key="navbarmenus-tracing">
            <MenuOptionItem id="navbarmenus-tracing" icon={"speed"} title={"Tracing"} />
          </Item>
          <Item key="navbarmenus-error">
            <MenuOptionItem id="navbarmenus-error" icon={"report"} title={"Error"} />
          </Item>
        </Section>
      )}
    </MenuTriggerButton>
  );
}
